import {
    Box, Flex, Text, Spinner, useToast,
    Stack, Heading, Button, Card, CardBody, CardFooter, Image,
    Grid, AlertDialog, AlertDialogOverlay, AlertDialogContent,
    AlertDialogHeader, AlertDialogBody, AlertDialogFooter, Badge, Textarea
} from '@chakra-ui/react';
import axiosClient from '../../../axios-client';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import empty from '../../../assets/images/emptyupdate.png'
import contentImg from '../../../assets/images/newContent.png'
import { IoTrash } from "react-icons/io5";

export default function GestNewContent() {
    const [newContents, setNewContents] = useState([]);
    const [loading, setLoading] = useState(true);
    const toast = useToast();
    const navigate = useNavigate()
    const { t } = useTranslation()
    const [isAlertOpen, setIsAlertOpen] = useState(false);

    const [editingId, setEditingId] = useState(null);
    const [editedText, setEditedText] = useState('');

    const [deleteId, setDeleteId] = useState(null);
    const cancelRef = React.useRef()

    const [showFullText, setShowFullText] = useState(false);

    const onOpenAlert = () => setIsAlertOpen(true);
    const onCloseAlert = () => setIsAlertOpen(false);

    useEffect(() => {
        async function fetchNewContents() {
            try {
                const { data } = await axiosClient.get('new-content/');
                setNewContents(data);
            } catch (error) {
                toast({
                    title: "Erreur lors du chargement",
                    description: "Impossible de charger le nouveau contenu.",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                    position: "bottom-right"
                });
            } finally {
                setLoading(false);
            }
        }
        fetchNewContents();
    }, []);

    if (loading) {
        return (
            <Flex justify="center" align="center" height="100vh">
                <Spinner size="xl" />
            </Flex>
        );
    }

    const handleOnDragEnd = (result) => {
        if (!result.destination) return;

        const reorderedItems = Array.from(newContents);
        const [movedItem] = reorderedItems.splice(result.source.index, 1);
        reorderedItems.splice(result.destination.index, 0, movedItem);

        setNewContents(reorderedItems);

        updateDatabase(reorderedItems);
    };

    const updateDatabase = (newOrder) => {
        const updatedOrder = newOrder.map((item, index) => ({
            id: item.id,
            order: index,
        }));

        axiosClient.post('/new-content/update_order/', updatedOrder)
            .catch(() => {
                toast({
                    description: "Erreur lors de la mise à jour de l’ordre.",
                    status: "error",
                    duration: 2000,
                    isClosable: true,
                    position: "bottom-right"
                })
            });
    };

    const handleSave = async (id) => {
        try {
            await axiosClient.put(`/new-content/update/${id}/`, {
                text: editedText,
            });

            setNewContents(prev =>
                prev.map(item =>
                    item.id === id ? { ...item, text: editedText } : item
                )
            );

            setEditingId(null);
            toast({
                description: "Texte mis à jour avec succès.",
                status: "success",
                duration: 3000,
                variant: 'left-accent',
                isClosable: true,
                position: "bottom-right"
            });
        } catch (error) {
            toast({
                description: "La mise à jour du texte a échoué.",
                status: "error",
                variant: 'left-accent',
                duration: 3000,
                isClosable: true,
                position: "bottom-right"
            });
        }
    };

    const handleDelete = (id = deleteId) => {
        if (!id) return;
        axiosClient.delete(`/new-content/delete/${id}/`)
            .then(() => {
                setNewContents((prevData) => prevData.filter((row) => row.id !== id));
                toast({
                    description: t("Le contenu est supprimé avec succès"),
                    status: 'success',
                    variant: 'left-accent',
                    duration: 2000,
                    isClosable: true,
                    position: 'bottom-right'
                })
            })
            .catch(() => {
                toast({
                    description: "Erreur lors de la suppression",
                    status: "error",
                    duration: 2000,
                    isClosable: true,
                    position: "bottom-right"
                });
            });
        onCloseAlert()
    }

    return (
        <Box>
            <Flex>
                <Box w="100%" display={"flex"} justifyContent="space-between" flexDirection={{ base: 'column', md: "row" }} mt={2} mb={{ base: 0, md: 5 }}>
                    <Text fontFamily='Plus Jakarta Display' as="cite" fontSize={{ base: "18px", md: "20px", lg: "25px" }} color="gray">
                        <span onClick={() => navigate('/')} style={{ cursor: 'pointer' }} onMouseEnter={e => e.target.style.textDecoration = "underline"} onMouseLeave={e => e.target.style.textDecoration = "none"} >
                            {t("Accueil")}
                        </span>  / <span>{t("Nouveautés")}</span>
                    </Text>
                </Box>
            </Flex>

            <Flex direction='column' mt={{ sm: '25px', md: '0px' }} p={{ base: 0, lg: 2 }}>
                {newContents.length > 0 ?
                    <Grid templateColumns={{ sm: "1fr", md: "1fr", lg: "2fr 1fr " }} justify={"center"} gap='22px'>
                        <DragDropContext onDragEnd={handleOnDragEnd}>
                            <Droppable droppableId="droppable">
                                {(provided) => (
                                    <Box ref={provided.innerRef} {...provided.droppableProps} mt={2} display="flex" flexDirection="column" gap={5}>
                                        {newContents.map((item, index) => {
                                            const content = item.contentInfo || item.content; // adjust based on serializer

                                            return (
                                                <Draggable key={item.id} draggableId={item.id.toString()} index={index}>
                                                    {(provided) => (
                                                        <Card
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            key={index}
                                                            direction={{ base: 'column', sm: 'row' }}
                                                            overflow='hidden'
                                                            variant='outline'
                                                            w={"auto"}
                                                        >
                                                            <Image
                                                                objectFit='cover'
                                                                w={{ base: '100%', sm: '150px' }}
                                                                h={{ base: "150px", sm: "100px" }}
                                                                src={content?.image_Hquality || content?.image}
                                                                alt='Media'
                                                            />

                                                            <Stack w={"100%"}>
                                                                <CardBody gap={2}  w={"100%"} display={"flex"} justifyContent={"space-between"} >
                                                                    <Heading size='md'>{content?.title} <Badge borderRadius={"md"} colorScheme='orange'>{content?.type}</Badge>

                                                                    </Heading>
                                                                    <Button flexShrink={0} display={{ base: "none", lg: 'block' }} size={"sm"} onClick={() => {
                                                                        setDeleteId(item.id);
                                                                        onOpenAlert();
                                                                    }} variant='outline' colorScheme='red'>
                                                                        {t('Supprimer')}
                                                                    </Button>
                                                                    <Flex 
                                                                    h={'fit-content'}
                                                                    
                                                                    onClick={() => {
                                                                        setDeleteId(item.id);
                                                                        onOpenAlert();
                                                                    }} display={{ base: "block", lg: 'none' }}>
                                                                        <IoTrash color='red' cursor={"pointer"} size={20} />
                                                                    </Flex>

                                                                    {/* <Text py='2'>
                                                                            {item.contentInfo.description && item.contentInfo.description.length > 100 && !showFullText
                                                                                ? `${item.contentInfo.description.substring(0, 100)}...`
                                                                                : item.contentInfo.description}
                                                                            {item.contentInfo.description && item.contentInfo.description.length > 100 && (
                                                                                <Button
                                                                                    variant="link"
                                                                                    colorScheme="blue"
                                                                                    ml={2}
                                                                                    size="sm"
                                                                                    onClick={() => setShowFullText(!showFullText)}
                                                                                >
                                                                                    {showFullText ? 'Voir moins' : 'Voir plus'}
                                                                                </Button>
                                                                            )}
                                                                        </Text> */}
                                                                </CardBody>

                                                                {/* <CardFooter pt={0} gap={2} display={"flex"} alignItems={"end"} w={"100%"} justifyContent={"end"}>
                                                                    <Button onClick={() => {
                                                                                setDeleteId(item.id);
                                                                                onOpenAlert();
                                                                            }} variant='outline' colorScheme='red'>
                                                                                {t('Supprimer')}
                                                                            </Button>
                                                                </CardFooter> */}
                                                            </Stack>
                                                        </Card>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {provided.placeholder}
                                    </Box>
                                )}
                            </Droppable>
                        </DragDropContext>
                        <Flex display={{ base: "none", lg: "flex" }} justify={"center"} align={'center'}>
                            <Image opacity={0.8} src={contentImg} style={{ height: "280px", width: "280px", objectFit: 'contain' }} />
                        </Flex>
                    </Grid>
                    :
                    <Flex mb={'10'} mt={20} gap={2} flexDirection={'column'} h={"100%"} w={"100%"} alignItems={"center"} justifyContent={"center"}>
                        <Text color={'gray.500'} fontWeight={"semibold"}>{t("Aucun nouveau contenu pour le moment.")}</Text>
                        <Image opacity={0.4} w={{ base: "150px", md: "200px", lg: "200px" }} src={empty} />
                    </Flex>
                }
            </Flex>

            <AlertDialog
                closeOnOverlayClick={false}
                isOpen={isAlertOpen}
                leastDestructiveRef={cancelRef}
                onClose={onCloseAlert}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                            {t('Supprimer')}
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            <Text>{t('êtes-vous sûr ? Vous ne pourrez pas annuler cette action ultérieurement.')}</Text>
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button size={"sm"} ref={cancelRef} onClick={onCloseAlert}>
                                {t('Annuler')}
                            </Button>
                            <Button size={"sm"} onClick={() => handleDelete(deleteId)} colorScheme='red' ml={3}>
                                {t('Supprimer')}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
}
